if (!customElements.get('main-product')) {
  class MainProduct extends HTMLElement {
    abortController = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];
    isQuickviewModal = false;

    constructor() {
      super();
    }

    get cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    get form() {
      return this.querySelector('form');
    }

    get submitButton() {
      return this.form.elements['add'];
    }

    get optionValues() {
      return Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select'),
        (el) => ({
          name: el.dataset.name,
          value: el.value,
          id: el.dataset.optionValueId || el.selectedOptions?.[0].dataset.optionValueId
        }),
      );
    }

    get selectedVariant() {
      const selectedVariant = this.querySelector(
        '[data-selected-variant]'
      )?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    connectedCallback() {
      this.isQuickviewModal = this.closest('modal-dialog');

      this.initializeProductSwapUtility();
      this.attachEvents();
    }

    /**
     * Attach events.
     */
    attachEvents() {
      // Form submit.
      this.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );

      // Variant change.
      this.addEventListener('change', this.onVariantChange);
    }

    /**
     * Form submit event callback.
     *
     * @param {Event} event
     */
    onSubmitHandler(event) {
      if (event.target !== this.form) {
        return;
      }

      event.preventDefault();

      this.submitButton.classList.add('disabled');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const formData = new FormData(this.form);
      if (this.cartDrawer) {
        formData.append(
          'sections',
          this.cartDrawer
            .getSectionsToRender()
            .map(section => section.section)
        );
      }
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.json())
        .then(response => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          if (
            this.dataset.cartType == 'page' ||
            Shopify.template == 'cart'
          ) {
            window.location.reload();
          } else if (this.dataset.cartType == 'drawer') {
            this.cartDrawer?.renderContents(response);
            document.querySelector('#Product-Quickview-Modal[open]')?.hide();
          }
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    /**
     * Option change event callback.
     *
     * @param {Event} event
     */
    async onVariantChange(event) {
      if (
        event.target.type === 'number' ||
        event.target.name.includes('selling_plan') || 
        event.target.parentElement.nodeName == 'GIFT-CARD-RECIPIENT-FORM'
      ) {
        return;
      }

      const productUrl =
        event.target.dataset.productUrl ||
        this.pendingRequestUrl ||
        event.target.selectedOptions?.[0].dataset.productUrl ||
        this.dataset.url;

      this.pendingRequestUrl = productUrl;
      const shouldSwapProduct = this.dataset.url !== productUrl;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(
          productUrl,
          this.optionValues.map(value => value.id),
          shouldSwapProduct
        ),
        targetId: event.target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(!this.closest('modal-dialog'))
          : this.handleUpdateProductInfo(this.closest('modal-dialog'))
      });
    }

    /**
     * Update live regions.
     *
     * @param {Document} html
     */
    updateLiveRegions(html) {
      this.querySelectorAll('[data-product-info]').forEach(
        (element) => {
          HTMLUpdateUtility.viewTransition(
            element,
            html.querySelector(`[data-product-info="${element.getAttribute('data-product-info')}"]`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }
      );
    }

    /**
     * Build request URL with params.
     *
     * @param {String} url
     * @param {Array} optionValues
     * @param {Boolean} shouldFetchFullPage
     * @returns {String}
     */
    buildRequestUrlWithParams(
      url,
      optionValues,
      shouldFetchFullPage
    ) {
      const params = [];
      const sectionId = this?.dataset.sectionId;

      sectionId &&
        !shouldFetchFullPage &&
        params.push(`section_id=${sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    /**
     * Initialize product swap utility. Used with combined listings.
     */
    initializeProductSwapUtility() {
      this.postProcessHtmlCallbacks.push(newNode => {
        window?.Shopify?.PaymentButton?.init();
      });
    }

    /**
     * Render product info.
     *
     * @param {String} requestUrl
     * @param {String} targetId
     * @param {Function} callback
     */
    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then(response => response.text())
        .then(responseText => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch(error => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    /**
     * Handle swap product.
     *
     * @param {Boolean} updateFullPage
     * @returns {Function}
     */
    handleSwapProduct(updateFullPage) {
      return html => {
        if (!updateFullPage) {
          HTMLUpdateUtility.viewTransition(
            this,
            html.querySelector('main-product'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          return;
        }

        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

        HTMLUpdateUtility.viewTransition(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
        this.updateURL(html.querySelector('main-product').getAttribute('data-url'));
      };
    }

    /**
     * Handle update product info.
     *
     * @returns {Void}
     */
    handleUpdateProductInfo(isQuickviewModal) {
      return html => {
        this.updateLiveRegions(html);

        if (this.selectedVariant) {
          this.updateMedia();
          !isQuickviewModal && this.updateURL();
          return;
        }

        this.setUnavailable();
      };
    }

    /**
     * Set unavailable.
     *
     * @returns {Void}
     */
    setUnavailable() {
      if (this.submitButton) {
        this.submitButton.textContent = window.variantStrings.unavailable;
      }
      this.querySelector('[data-product-info="price"]').classList.add('visually-hidden');
    }

    /**
     * Update URL.
     *
     * @returns {Void}
     */
    updateURL(newUrl = null) {
      if (!this.selectedVariant) return;
      const params = new URLSearchParams(window.location.search);
      params[params.has('variant') ? 'set' : 'append'](
        'variant',
        this.selectedVariant.id,
      );
      window.history.replaceState(
        {},
        '',
        `${newUrl ?? this.dataset.url}?${params.toString()}`,
      );
    }

    /**
     * Update featured media.
     *
     * @returns {void}
     */
    updateMedia() {
      if (!this.selectedVariant?.featured_media) return;

      const productMedia = this.querySelector('product-media');
      productMedia.setActiveMedia(
        this.selectedVariant.featured_media.id
      );
    }

    /**
     * Handles error message.
     *
     * @param {String|Object} errorMessage Comes from Shopify.
     * @returns {void}
     */
    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;

      errorWrapper.classList.toggle('hidden', !errorMessage);

      if (typeof errorMessage === 'object') {
        for (const [key, value] of Object.entries(errorMessage)) {
          errorWrapper.textContent = `${key.charAt(0).toUpperCase() + key.slice(1)} ${value}`
        }
      } else {
        errorWrapper.textContent = errorMessage || '';
      }
    }
  }
  customElements.define('main-product', MainProduct);
}
